import { useEffect, useMemo, useState } from "react";
import {
  ListChecks,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ShieldAlert,
  BellRing,
  Printer,
  Activity,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, hasRole } from "../utils/auth";
import { loadPrograms, flattenSubs } from "../utils/programStorage";
import { loadAssignments } from "../utils/assignmentStorage";
import { computeHighlight } from "../utils/progressUtils";
import { scopeSubsForUser, scopePeopleForUser, labelForRole } from "../utils/scopeUtils";
import RoleBadge from "../components/Layout/RoleBadge";
import StatCard from "../components/Dashboard/StatCard";
import OverallProgressCard from "../components/Dashboard/OverallProgressCard";
import ProgramCapaianChart from "../components/Dashboard/ProgramCapaianChart";
import StatusDonutChart from "../components/Dashboard/StatusDonutChart";
import DeadlineTable from "../components/Dashboard/DeadlineTable";
import BebanTimChart from "../components/Dashboard/BebanTimChart";
import ProgressKegiatanRekap from "../components/Dashboard/ProgressKegiatanRekap";
import DumasTable from "../components/Dashboard/DumasTable";

function getGreeting() {
  const h = new Date().getHours();
  if (h < 11) return "Selamat pagi";
  if (h < 15) return "Selamat siang";
  if (h < 18) return "Selamat sore";
  return "Selamat malam";
}

/* ── Inspector Bird SVG Mascot ──────────────────────────────── */
function InspectorBird() {
  return (
    <svg
      viewBox="0 0 180 220"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full drop-shadow-xl"
      aria-label="Maskot Burung Inspektur"
    >
      {/* ── Shadow ── */}
      <ellipse cx="90" cy="213" rx="50" ry="9" fill="#94a3b8" opacity="0.25" />

      {/* ── Tail feathers ── */}
      <ellipse cx="90" cy="182" rx="26" ry="13" fill="#1d4ed8" />
      <ellipse cx="68" cy="178" rx="13" ry="9" fill="#1a4aad" transform="rotate(-20 68 178)" />
      <ellipse cx="112" cy="178" rx="13" ry="9" fill="#1a4aad" transform="rotate(20 112 178)" />

      {/* ── Body ── */}
      <ellipse cx="90" cy="142" rx="46" ry="52" fill="#3b82f6" />

      {/* ── Left wing ── */}
      <ellipse cx="48" cy="133" rx="18" ry="36" fill="#2563eb" transform="rotate(-18 48 133)" />

      {/* ── Right wing ── */}
      <ellipse cx="132" cy="133" rx="18" ry="36" fill="#2563eb" transform="rotate(18 132 133)" />

      {/* ── White belly ── */}
      <ellipse cx="90" cy="148" rx="26" ry="35" fill="white" />

      {/* ── Clipboard (held by right wing) ── */}
      <rect x="118" y="108" width="34" height="46" rx="5" fill="white" stroke="#e2e8f0" strokeWidth="1.5" />
      <rect x="130" y="101" width="10" height="13" rx="3" fill="#94a3b8" />
      <rect x="131" y="102" width="8" height="4" rx="1" fill="#64748b" />
      <line x1="123" y1="121" x2="147" y2="121" stroke="#cbd5e1" strokeWidth="1.5" />
      <line x1="123" y1="129" x2="147" y2="129" stroke="#cbd5e1" strokeWidth="1.5" />
      <line x1="123" y1="137" x2="140" y2="137" stroke="#cbd5e1" strokeWidth="1.5" />
      {/* Checkmark */}
      <circle cx="142" cy="143" r="6" fill="#10b981" />
      <polyline points="139,143 141.5,145.5 145.5,140" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />

      {/* ── Neck connector ── */}
      <ellipse cx="90" cy="100" rx="24" ry="14" fill="#3b82f6" />

      {/* ── Head ── */}
      <circle cx="90" cy="72" r="34" fill="#3b82f6" />

      {/* ── Inspector hat brim ── */}
      <rect x="54" y="40" width="72" height="10" rx="4" fill="#1e3a5f" />
      {/* Hat top */}
      <rect x="62" y="14" width="56" height="30" rx="6" fill="#1e3a5f" />
      {/* Hat golden band */}
      <rect x="62" y="34" width="56" height="8" rx="0" fill="#f59e0b" />
      {/* Hat shine */}
      <rect x="66" y="17" width="12" height="4" rx="2" fill="white" opacity="0.15" />

      {/* ── Round glasses (left) ── */}
      <circle cx="74" cy="70" r="13" fill="none" stroke="#1e3a5f" strokeWidth="2" />
      <circle cx="74" cy="70" r="10" fill="white" />
      <circle cx="74" cy="70" r="7" fill="#0f172a" />
      <circle cx="71" cy="67" r="2.5" fill="white" />

      {/* ── Round glasses (right) ── */}
      <circle cx="106" cy="70" r="13" fill="none" stroke="#1e3a5f" strokeWidth="2" />
      <circle cx="106" cy="70" r="10" fill="white" />
      <circle cx="106" cy="70" r="7" fill="#0f172a" />
      <circle cx="103" cy="67" r="2.5" fill="white" />

      {/* Glasses bridge */}
      <line x1="87" y1="70" x2="93" y2="70" stroke="#1e3a5f" strokeWidth="2" />
      {/* Ear pieces */}
      <line x1="61" y1="70" x2="53" y2="74" stroke="#1e3a5f" strokeWidth="2" />
      <line x1="119" y1="70" x2="127" y2="74" stroke="#1e3a5f" strokeWidth="2" />

      {/* ── Beak ── */}
      <path d="M 80 83 Q 90 96 100 83 Q 90 88 80 83" fill="#f59e0b" />

      {/* ── Cheek blushes ── */}
      <ellipse cx="62" cy="80" rx="7" ry="5" fill="#fda4af" opacity="0.5" />
      <ellipse cx="118" cy="80" rx="7" ry="5" fill="#fda4af" opacity="0.5" />

      {/* ── Gold star badge on belly ── */}
      <g transform="translate(77, 126)">
        <polygon
          points="13,0 16,8.5 25,8.5 18,14 20.5,22.5 13,17.5 5.5,22.5 8,14 1,8.5 10,8.5"
          fill="#f59e0b"
        />
        <polygon
          points="13,3 15.3,9.8 22.5,9.8 17,13.8 19.2,20.6 13,16.6 6.8,20.6 9,13.8 3.5,9.8 10.7,9.8"
          fill="#fbbf24"
        />
        <text x="13" y="15" textAnchor="middle" fontSize="6.5" fill="#92400e" fontWeight="800">
          IR
        </text>
      </g>

      {/* ── Feet ── */}
      <ellipse cx="72" cy="196" rx="14" ry="6" fill="#f59e0b" transform="rotate(-8 72 196)" />
      <ellipse cx="108" cy="196" rx="14" ry="6" fill="#f59e0b" transform="rotate(8 108 196)" />

      {/* ── Sparkles ── */}
      <g fill="#fbbf24" opacity="0.8">
        <polygon points="20,30 22,26 24,30 28,32 24,34 22,38 20,34 16,32" transform="scale(0.7) translate(10,-10)" />
        <polygon points="155,55 157,51 159,55 163,57 159,59 157,63 155,59 151,57" transform="scale(0.5)" />
      </g>
    </svg>
  );
}

/* ── Main Dashboard Component ───────────────────────────────── */
export default function Dashboard() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [users, setUsers] = useState([]);

  useEffect(() => {
    let mounted = true;
    loadPrograms().then((d) => { if (mounted) setPrograms(d); }).catch(() => {});
    loadAssignments().then((d) => { if (mounted) setAssignments(d); }).catch(() => {});
    api.get("/users")
      .then((res) => { if (mounted) setUsers(Array.isArray(res.data) ? res.data : []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const userById = useMemo(() => {
    const m = {};
    (Array.isArray(users) ? users : []).forEach((u) => (m[u.id] = u));
    return m;
  }, [users]);

  const allSubs  = useMemo(() => flattenSubs(programs), [programs]);
  const scopedSubs = useMemo(() => scopeSubsForUser(user, allSubs, assignments), [user, allSubs, assignments]);
  const scopedSubIds = useMemo(() => (hasRole(user, "IRBAN") ? null : new Set(scopedSubs.map((s) => s.id))), [user, scopedSubs]);
  const scopedPeople = useMemo(() => scopePeopleForUser(user, users, scopedSubs, assignments), [user, users, scopedSubs, assignments]);

  const summary = useMemo(() => {
    const count = (st) => scopedSubs.filter((s) => s.status === st).length;
    const totalProgress = scopedSubs.reduce((acc, s) => acc + (s.progress || 0), 0);
    const percent = scopedSubs.length > 0 ? Math.round(totalProgress / scopedSubs.length) : 0;
    const red = scopedSubs.filter((s) => computeHighlight(s).level === "RED").length;
    const auditSub = allSubs.find((s) => s.specialType === "AUDIT_INVESTIGASI");
    const auditAktif = auditSub && auditSub.status !== "SELESAI" ? 1 : 0;
    return {
      total: scopedSubs.length,
      selesai: count("SELESAI"),
      onProgress: count("ON_PROGRESS"),
      belumMulai: count("BELUM_MULAI"),
      terhambat: count("TERHAMBAT"),
      percent,
      tlMendesak: red,
      auditAktif,
    };
  }, [scopedSubs, allSubs]);

  const firstName = user?.name?.split(/[\s,]+/)[0] || "Pengguna";

  return (
    <div className="space-y-6" data-testid="dashboard-page">

      {/* ══════════════════════════════════════════
          HERO BANNER — Light sky-blue like Lingora
          ══════════════════════════════════════════ */}
      <div
        className="relative overflow-hidden rounded-3xl print:hidden"
        style={{
          background: "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 40%, #93c5fd 100%)",
        }}
      >
        {/* Decorative blobs */}
        <div
          className="absolute -top-12 -right-12 w-56 h-56 rounded-full opacity-40"
          style={{ background: "radial-gradient(circle, #7dd3fc, transparent)" }}
        />
        <div
          className="absolute bottom-0 left-1/4 w-40 h-40 rounded-full opacity-30"
          style={{ background: "radial-gradient(circle, #93c5fd, transparent)" }}
        />
        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: "radial-gradient(circle, #1e40af 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />

        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-6 md:p-8">
          {/* ── Left: greeting & actions ── */}
          <div className="flex-1 max-w-lg">
            {/* Pill badge */}
            <div className="inline-flex items-center gap-2 rounded-full bg-white/60 backdrop-blur-sm px-3 py-1 text-[11px] font-semibold text-blue-700 mb-3 border border-blue-200/60">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Dashboard Aktif · Program Pengawasan 2026
            </div>

            <h1 className="font-display text-2xl font-bold text-slate-900 md:text-3xl leading-snug">
              {getGreeting()},{" "}
              <span className="text-blue-600">{firstName}!</span>{" "}
              <span>👋</span>
            </h1>
            <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">
              Pantau progres program pengawasan, deadline, dan beban tim secara real-time.
            </p>

            {/* Action buttons */}
            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <a
                href="/progress"
                className="inline-flex items-center gap-2 rounded-xl text-[13px] font-semibold px-4 py-2 text-white transition-all hover:opacity-90 shadow-md"
                style={{ background: "linear-gradient(135deg, #1e3a5f, #2563eb)" }}
              >
                <Activity className="h-4 w-4" />
                Lihat Progress
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
              <button
                type="button"
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-xl bg-white/70 backdrop-blur-sm text-[13px] font-semibold px-4 py-2 text-slate-700 hover:bg-white transition-colors border border-white/80 shadow-sm"
              >
                <Printer className="h-4 w-4" />
                Cetak
              </button>
            </div>

            {/* Scope badge */}
            {user && (
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white/50 backdrop-blur-sm px-3 py-1.5 border border-blue-200/50"
                data-testid="dashboard-scope-card"
              >
                <RoleBadge role={user.role} />
                <span className="text-[11px] text-slate-600">{labelForRole(user.role)}</span>
              </div>
            )}
          </div>

          {/* ── Right: progress mini-card + bird mascot ── */}
          <div className="flex items-end gap-4 md:gap-6">
            {/* Progress card */}
            <div className="hidden sm:flex flex-col gap-1 bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/80 shadow-sm min-w-[160px]">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-0.5">
                Capaian Program
              </div>
              <div className="font-display text-4xl font-bold text-[#1e3a5f]">
                {summary.percent}%
              </div>
              <div className="h-1.5 w-full rounded-full bg-blue-100 overflow-hidden mt-1">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${summary.percent}%`,
                    background: "linear-gradient(90deg, #3b82f6, #10b981)",
                  }}
                />
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {summary.selesai}/{summary.total} sub selesai
              </div>
              <div className="mt-1.5 flex gap-2 flex-wrap">
                <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  {summary.selesai} selesai
                </span>
                <span className="flex items-center gap-1 text-[10px] text-blue-600 font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                  {summary.onProgress} berjalan
                </span>
              </div>
            </div>

            {/* ── Bird Mascot ── */}
            <div className="w-36 h-44 md:w-44 md:h-52 flex-shrink-0 select-none">
              <InspectorBird />
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          STAT CARDS — Lingora-style vivid gradients
          ══════════════════════════════════════════ */}
      <section
        aria-label="Ringkasan"
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6 print:grid-cols-3"
      >
        <StatCard
          icon={ListChecks}
          label="Total Sub-Kegiatan"
          value={summary.total}
          sublabel="Dalam cakupan Anda"
          color="blue"
          testId="stat-total"
        />
        <StatCard
          icon={CheckCircle2}
          label="Selesai"
          value={summary.selesai}
          color="emerald"
          testId="stat-selesai"
        />
        <StatCard
          icon={Clock}
          label="On Progress"
          value={summary.onProgress}
          color="violet"
          testId="stat-on-progress"
        />
        <StatCard
          icon={AlertTriangle}
          label="Terhambat"
          value={summary.terhambat}
          color="red"
          testId="stat-terhambat"
        />
        <StatCard
          icon={ShieldAlert}
          label="Audit Investigasi"
          value={summary.auditAktif}
          sublabel="P11c aktif"
          color="amber"
          testId="stat-audit-aktif"
        />
        <StatCard
          icon={BellRing}
          label="TL Mendesak"
          value={summary.tlMendesak}
          sublabel="Lewat deadline"
          color="rose"
          testId="stat-tl-mendesak"
        />
      </section>

      {/* ══════════════════════════════════════════
          MAIN CONTENT — 2-column layout
          ══════════════════════════════════════════ */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3 print:grid-cols-1">

        {/* Left: charts + table */}
        <div className="xl:col-span-2 space-y-4 print:col-span-1">
          {/* 3-card row: Progress ring · Status donut · Rekap Progress */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <OverallProgressCard
              percent={summary.percent}
              totalSubs={summary.total}
              selesai={summary.selesai}
            />
            <StatusDonutChart subs={scopedSubs} />
            <ProgressKegiatanRekap summary={summary} scopedSubs={scopedSubs} />
          </div>

          {/* Program capaian chart */}
          <ProgramCapaianChart programs={programs} scopedSubIds={scopedSubIds} />

          {/* Surat Masuk Dumas */}
          <DumasTable />
        </div>

        {/* Right: beban tim + deadline */}
        <div className="space-y-4">
          <BebanTimChart
            people={scopedPeople}
            scopedSubs={scopedSubs}
            assignments={assignments}
          />
          <DeadlineTable subs={scopedSubs} assignments={assignments} userById={userById} />
        </div>
      </div>
    </div>
  );
}
